'use client';

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';

type Props = {
  /** Nome do capítulo que foi baixado (null = não mostrar) */
  chapterName: string | null;
  /** Duração em ms antes de sumir (default 1000) */
  duration?: number;
  onClose?: () => void;
};

/**
 * Notificação no canto inferior: avisa que o capítulo foi baixado.
 * No mobile fica acima da navbar inferior; no desktop no canto inferior.
 */
export default function DownloadNotification({
  chapterName,
  duration = 1000,
  onClose,
}: Props) {
  useEffect(() => {
    if (!chapterName || !onClose) return;
    const t = setTimeout(onClose, duration);
    return () => clearTimeout(t);
  }, [chapterName, duration, onClose]);

  if (!chapterName || typeof document === 'undefined') return null;

  const content = (
    <div className="fixed bottom-20 right-4 z-[9999] w-[min(340px,calc(100vw-2rem))] md:bottom-6">
      <div
        role="alert"
        className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/70 px-4 py-3 backdrop-blur-xl"
        style={{
          animation: 'downloadNotifIn 0.3s ease-out forwards',
        }}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-500/20 text-green-400">
          <Check size={20} strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1 py-0.5">
          <p className="text-sm font-semibold text-white">Capítulo baixado!</p>
          <p className="mt-0.5 truncate text-xs text-white/70" title={chapterName}>
            {chapterName}
          </p>
        </div>
      </div>
      <style dangerouslySetInnerHTML={{
        __html: `@keyframes downloadNotifIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`,
      }} />
    </div>
  );

  return createPortal(content, document.body);
}
